import { buildingID, siteBuildings, siteLevels } from './siteBuildings.js';
import { withStairContext, stairSpec, stairFootprint } from './stairs.js';
import { polyBBox } from './geometry.js';
import { personDimensions, presentInScene } from './sceneObjects.js';

/** The active plan lives at the document root; other levels are snapshots.
 * Never write view state or reference geometry into any of those plans. */
export function buildingLevels(S) {
  return siteLevels(S).map(f => ({ ...f, elevation: f.elevation || 0, name: siteBuildings(S).length > 1 ? `${siteBuildings(S).find(b => b.id === f.buildingId)?.name || 'Building'} · ${f.name}` : f.name })).sort((a, b) => a.elevation - b.elevation || String(a.id).localeCompare(String(b.id)));
}

function footprints(plan) {
  const indoor = (plan.areas || []).filter(a => a.kind === 'floor' && a.environment !== 'outdoor' && a.pts.length >= 3);
  const shells = indoor.filter(a => a.purpose === 'building');
  // Older projects used ordinary indoor areas before the shell tool existed.
  return shells.length ? shells : indoor;
}

/** Project the ground footprint onto an individual upper/basement work plane.
 * Only use actual footprint polygons, never outdoor boundaries or a bounding box.
 * The legacy ground ID also supports projects using an absolute elevation
 * datum. Otherwise use the closest-to-zero shell and identify its source. */
export function shellReferences(S) {
  const sources = buildingLevels(S).filter(f => (f.buildingId || buildingID(f.plan)) === buildingID(S) && footprints(f.plan).length);
  if (!sources.length) return [];
  sources.sort((a, b) => Math.abs(a.elevation) - Math.abs(b.elevation) || b.elevation - a.elevation || String(a.id).localeCompare(String(b.id)));
  const datum = sources.find(f => Math.abs(f.elevation) < 0.001)?.elevation ?? sources.find(f => f.id === 'ground')?.elevation ?? sources[0].elevation;
  if (Math.abs((S.elevation || 0) - datum) < 0.001) return [];
  return sources.filter(f => Math.abs(f.elevation - datum) < 0.001 && f.id !== S.activeFloorId)
    .flatMap(f => footprints(f.plan).map(a => ({ id: `${f.id}:${a.id}`, floorId: f.id, floorName: f.name, elevation: f.elevation, name: a.name || 'Building shell', pts: a.pts })));
}

export function withReferenceBounds(bounds, references) {
  const pts = references.flatMap(r => r.pts);
  if (bounds) pts.push({ x: bounds.x0, y: bounds.y0 }, { x: bounds.x1, y: bounds.y1 });
  return pts.length ? polyBBox(pts) : null;
}

export function levelExtents(S, { showRoofs = true } = {}) {
  const points = (S.areas || []).flatMap(a => a.pts);
  let top = 0, bottom = -0.16;
  if (showRoofs) for (const a of S.areas || []) top = Math.max(top, (a.roofHeight || 0) + (a.roofHeight ? 0.2 : 0));
  for (const w of S.walls || []) {
    points.push({ x: w.x1, y: w.y1 }, { x: w.x2, y: w.y2 }); top = Math.max(top, w.h || 0);
  }
  for (const c of [...(S.cams || []), ...(S.equipment || []), ...(S.lights || [])]) { points.push(c); top = Math.max(top, c.h || 0); }
  for (const o of S.obstacles || []) {
    if (!presentInScene(o, S.sceneState)) continue;
    const radius = Math.hypot(o.width || 1, o.depth || 1) / 2;
    points.push({ x: o.x - radius, y: o.y - radius }, { x: o.x + radius, y: o.y + radius });
    top = Math.max(top, (o.bottom || 0) + (o.height || 1));
  }
  for (const t of S.targets || []) {
    points.push({ x: t.x - 2.5, y: t.y - 2.5 }, { x: t.x + 2.5, y: t.y + 2.5 }); top = Math.max(top, t.kind === 'vehicle' ? 1.8 : personDimensions(S, t).top);
  }
  for (const stair of S.stairs || []) {
    const c = stairSpec(S, stair); if (!c || (S.elevation || 0) < c.lower.elevation || (S.elevation || 0) > c.upper.elevation) continue;
    points.push(...stairFootprint(stair)); bottom = Math.min(bottom, c.lower.elevation - (S.elevation || 0) - 0.16); top = Math.max(top, c.upper.elevation - (S.elevation || 0) + 1);
  }
  return { bounds: points.length ? polyBBox(points) : null, top, bottom };
}

export function buildingLayout(S, { hidden = [], gap = 0, showRoofs = true } = {}) {
  const levels = buildingLevels(S);
  // Separate each building's floors independently, without changing saved
  // elevations or shifting a building when another building gains a floor.
  const displayElevation = f => {
    const elevations = [...new Set(levels.filter(level => level.buildingId === f.buildingId).map(level => level.elevation))];
    const datum = elevations.reduce((a,b) => Math.abs(b) < Math.abs(a) ? b : a, elevations[0]);
    return f.elevation + (elevations.indexOf(f.elevation) - elevations.indexOf(datum)) * gap;
  };
  const visible = levels.filter(f => !hidden.includes(f.id)).map(f => ({
    ...f, displayElevation: displayElevation(f), ...levelExtents(withStairContext(S, f), { showRoofs })
  }));
  let bounds = null, bottom = Infinity, top = -Infinity;
  for (const f of visible) {
    if (!f.bounds) continue;
    bounds = withReferenceBounds(bounds, [{ pts: [{ x: f.bounds.x0, y: f.bounds.y0 }, { x: f.bounds.x1, y: f.bounds.y1 }] }]);
    bottom = Math.min(bottom, f.displayElevation + f.bottom); top = Math.max(top, f.displayElevation + f.top);
  }
  return { levels: visible, bounds: bounds ? { ...bounds, bottom, top } : null };
}
